import React, { ReactElement, useState } from 'react'
import styled from 'styled-components'
import { useDropzone } from 'react-dropzone'
import {
  IImageUploadResponse,
  ImageType,
  ImageFile,
  IPixelAspect,
} from './imageTypes'
import ImageCrop from './ImageCrop'
import { resizeFile } from './imageResize'
import theme from '../lib/theme'
import colors from '../lib/colors'
import Loading from '../components/Loading'
import { alertError, alertInfo } from '../components/Notifications'
import Button, { ButtonStyle } from '../buttons/Button'
import FieldStyles from '../components/FieldStyles'

const StyledDiv = styled.div`
  font-family: ${theme.fontFamily};
  color: ${colors.black};
  background-color: ${colors.lightestBlue};
  border: 2px dashed ${colors.lightBlue};
  border-radius: 4px;
  min-width: 200px;
  min-height: 120px;
  width: 100%;
  display: inline-flex;
  justify-content: center;
  padding: 10px;
  margin-bottom: 2px;
  box-sizing: border-box;

  &.error {
    border-color: ${colors.red};
    background-color: ${colors.lightestRed};
  }
  .upload-controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    p {
      margin: 0;
    }
    width: 50%;
  }
`
const Preview = styled.div`
  background-image: url(${(props): string => props.theme});
  width: 50%;
  height: auto;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 5px;
  box-sizing: border-box;
`
const StyledLoading = styled(Loading)`
  width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`
type UploadImageFieldProps = {
  isError?: boolean
  maxNumberOfFiles?: number
  imageType: ImageType
  label?: string
  subtitle?: string
  onSuccess: (response: IImageUploadResponse) => void
  previewUrl?: string
  canCrop?: boolean
  cropPixelAspect?: IPixelAspect
  minimumAspect?: IPixelAspect
  upload: (data: {
    image: Blob
    imageType: ImageType
  }) => Promise<IImageUploadResponse | false>
  className?: string
  noResizeOrConvert?: true
  isPng?: true
}

// maximum dimensions that we scale the image to before cropping/upload
// (ensures the image crop box fits on the screen and stops huge images being uploaded)
const SCALE_TO_MAX_PX = { x: 1300, y: 700 }
// minimum upload size (also affects the minimum size we can crop to)
const MIN_IMAGE_SIZE = { x: 200, y: 100 }
// not that important, as we resize and compress the image before upload
const MAX_FILE_SIZE_MB = 30

function UploadImageField(props: UploadImageFieldProps): ReactElement {
  const {
    isError,
    imageType,
    maxNumberOfFiles = 1,
    label,
    subtitle,
    onSuccess,
    previewUrl,
    canCrop,
    cropPixelAspect,
    minimumAspect = MIN_IMAGE_SIZE,
    upload,
    className,
    noResizeOrConvert,
    isPng,
  } = props

  const croppingEnabled = cropPixelAspect || canCrop

  const [cropImage, setCropImage] = useState<ImageFile | false>(false)
  const [loading, setLoading] = useState(false)

  const uploadFile = async (file: File): Promise<void> => {
    setLoading(true)
    const result = await upload({ image: file, imageType })
    if (result) {
      onSuccess(result)
      alertInfo(`"${file.name}" uploaded`)
    }
    setLoading(false)
  }

  const fileHandler = async (file: File): Promise<void> => {
    setLoading(true)

    if (isPng && file.type !== 'image/png') {
      alertError('Image must be PNG')
      setLoading(false)
      return
    }

    if (file.type === 'image/x-icon' || noResizeOrConvert) {
      await uploadFile(file)
      setLoading(false)
      return
    }

    // convert image to reasonable size (dimensions and file size) before cropping or uploading
    const resizedImageFile = await resizeFile(
      file,
      SCALE_TO_MAX_PX.x,
      SCALE_TO_MAX_PX.y,
      isPng ? 'PNG' : 'JPEG',
    )

    if (resizedImageFile) {
      if (!croppingEnabled) {
        uploadFile(resizedImageFile)
      } else {
        // load the image to find out its dimensions for cropping
        const sizeTestImage = new Image()
        sizeTestImage.src = URL.createObjectURL(resizedImageFile)
        sizeTestImage.addEventListener('load', () => {
          const x = sizeTestImage.width
          const y = sizeTestImage.height
          if (x < minimumAspect.x || y < minimumAspect.y) {
            alertError('Image too small')
          } else {
            setCropImage({
              file: resizedImageFile,
              name: resizedImageFile.name.toLowerCase(),
              dimensions: { x, y },
            })
          }
        })
      }
    } else {
      alertError('There was an error resizing the image')
    }
    setLoading(false)
  }

  const {
    getRootProps, getInputProps, open, isDragActive,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: maxNumberOfFiles,
    accept: 'image/*',
    maxSize: MAX_FILE_SIZE_MB * 1048576, // MB to Bytes
    onDrop: (acceptedFiles, rejectedFiles) => {
      acceptedFiles.map((file) => fileHandler(file))
      rejectedFiles.forEach((file) => {
        file.errors.forEach((err) => {
          if (err.code === 'too-many-files') {
            alertError(`too many files - max ${maxNumberOfFiles}`)
          }
          if (err.code === 'file-invalid-type') {
            alertError(
              `"${file.file.name}" is the wrong type - expecting image`,
            )
          }
          if (err.code === 'file-too-large') {
            alertError(
              `"${file.file.name}" is too large - max size ${MAX_FILE_SIZE_MB}MB`,
            )
          }
        })
      })
    },
  })

  const plural = maxNumberOfFiles > 1 ? 's' : ''
  const uploadButtonText = `Upload${previewUrl ? ' new ' : ' '}image${plural}`

  const renderControls = isDragActive ? (
    <strong>Drop files here...</strong>
  ) : (
    <React.Fragment>
      <Button onClick={open} buttonStyle={ButtonStyle.Action}>
        {uploadButtonText}
      </Button>
      <br />
      <span>
        or drag and drop
        {maxNumberOfFiles > 1 ? ' them ' : ' it '}
        here
      </span>
    </React.Fragment>
  )

  return (
    <React.Fragment>
      <FieldStyles label={label} subtitle={subtitle} className={className}>
        <StyledDiv {...getRootProps()} className={isError && 'error'}>
          <input {...getInputProps()} />
          <div className="upload-controls">
            {loading ? 'Uploading...' : renderControls}
          </div>
          {!loading && previewUrl && <Preview theme={previewUrl} />}
          {loading && <StyledLoading />}
        </StyledDiv>
      </FieldStyles>
      {cropImage && (
        <ImageCrop
          imageToCrop={cropImage}
          setCropImage={setCropImage}
          cropPixelAspect={cropPixelAspect}
          saveCrop={(file): Promise<void> => uploadFile(file)}
          cropPixelMinimumAspect={minimumAspect}
          isPng={isPng}
        />
      )}
    </React.Fragment>
  )
}

export default UploadImageField
